import { useCallback, useEffect, useMemo } from 'react';
import { UseFormReturn, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { useEmployeeLoginData } from '~/data/employee';
import { useYupValidationResolver } from '~/hooks/useYupValidationResolver';

type ChangeLoginMethodFormValues = {
  loginIsExternal: boolean;
};

type UseChangeLoginMethodDialogOutput = {
  form: UseFormReturn<ChangeLoginMethodFormValues>;
  resetForm: () => void;
};

export const useChangeLoginMethodDialog =
  (): UseChangeLoginMethodDialogOutput => {
    const { employeeAdminLoginData } = useEmployeeLoginData();

    const initialFieldValues: ChangeLoginMethodFormValues = useMemo(() => {
      return {
        loginIsExternal: employeeAdminLoginData.fLoginIsExternal || false,
      };
    }, [employeeAdminLoginData.fLoginIsExternal]);

    const validationSchema = useMemo(() => {
      return yup
        .object()
        .shape({
          loginIsExternal: yup.bool().required(),
        })
        .required();
    }, []);

    const resolver = useYupValidationResolver(validationSchema);

    const form = useForm<ChangeLoginMethodFormValues>({
      mode: 'all',
      defaultValues: initialFieldValues,
      resolver: resolver as never,
    });
    const { reset } = form;

    const resetForm = useCallback(() => {
      reset(initialFieldValues);
    }, [reset, initialFieldValues]);

    useEffect(() => {
      resetForm();
    }, [resetForm]);

    return {
      form,
      resetForm,
    };
  };
